import axios, { AxiosError } from "axios";
import { defaultRequestInterceptors, defaultResponseInterceptors } from "./config";

import type { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from "./types";
import { ElMessage } from "element-plus";

/**
 * 请求超时时间
 */
const REQUEST_TIMEOUT = 60000;

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH;

// abortControllerMap 映射存储每个请求的控制器
const abortControllerMap: Map<string, AbortController> = new Map();

// 创建axios实例 设置了超时时间和基础URL
const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL,
});

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  // AbortController管理请求的取消
  // 当请求发出时，创建一个新的AbortController并将其信号绑定到请求中，允许请求在需要时被取消。
  const controller = new AbortController();
  const url = res.url || "";
  // 将请求URL与控制器存储在abortControllerMap中，如果使用了模拟数据，会调整URL以匹配实际的请求路径。
  res.signal = controller.signal;
  abortControllerMap.set(url, controller);

  return res;
});
axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    // 当响应到达时，从abortControllerMap中删除对应的控制器，避免内存泄漏。
    const url = res.config.url || "";
    abortControllerMap.delete(url);
    // 这里不能做任何处理，否则后面的 interceptors 拿不到完整的上下文了
    return res;
  },
  (error: AxiosError) => {
    console.log("err： " + error); // for debug
    ElMessage.error(error.message);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.request.use(defaultRequestInterceptors);
axiosInstance.interceptors.response.use(defaultResponseInterceptors);

const service = {
  /**
   *  发送请求
   * @param config
   */
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any);
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res);
        })
        .catch((err: any) => {
          reject(err);
        });
    });
  },
  /**
   * 取消一个或多个特定URL的请求。
   * @param url
   */
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url];
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort();
      abortControllerMap.delete(_url);
    }
  },
  /**
   * 取消所有当前活动的请求
   */
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort();
    }
    abortControllerMap.clear();
  },
};

export default service;
